import { AxiosError } from 'axios';

/**
 * Canonical backend error envelope (see backend/src/middleware/errorHandler.js):
 *   { success: false, error: <code>, message, requestId, details? }
 *
 * For ValidationError, `details` is an array of `{ path, message }`.
 */
interface ApiErrorBody {
  success?: false;
  error?: string;
  message?: string;
  requestId?: string;
  details?: Array<{ path?: string; message?: string }> | string;
}

const isAxiosLike = (e: unknown): e is AxiosError<ApiErrorBody> =>
  typeof e === 'object' && e !== null && 'isAxiosError' in e;

/**
 * Pick the most user-friendly message out of an axios error.
 *
 * Priority:
 *   1. ValidationError details, joined as "<field>: <msg>".
 *   2. data.message (the human-friendly text from the canonical envelope).
 *   3. data.error (the error code).
 *   4. error.message (axios fallback, e.g. "Network Error").
 *   5. The provided fallback string.
 */
export const formatApiError = (
  error: unknown,
  fallback = 'Something went wrong'
): string => {
  if (!isAxiosLike(error)) {
    if (error instanceof Error) return error.message || fallback;
    return fallback;
  }

  const data = error.response?.data;

  if (data && Array.isArray(data.details) && data.details.length > 0) {
    const lines = data.details
      .map((d) => {
        const path = (d.path || '').replace(/^body\./, '');
        return path ? `${path}: ${d.message || 'invalid'}` : d.message || 'invalid';
      })
      .filter(Boolean);
    if (lines.length > 0) {
      const head = data.message && data.message !== 'Invalid request data' ? `${data.message}: ` : '';
      return `${head}${lines.join('; ')}`;
    }
  }

  if (data?.message) return data.message;
  if (data?.error) return data.error;
  if (error.message) return error.message;
  return fallback;
};

/**
 * Returns a per-field error map from a ValidationError envelope, e.g.:
 *   { 'due_date': 'Expected YYYY-MM-DD or ISO 8601 datetime' }
 *
 * Useful for inline form-field error highlighting. Returns {} when the error
 * isn't a structured ValidationError.
 */
export const fieldErrorsFromApiError = (error: unknown): Record<string, string> => {
  if (!isAxiosLike(error)) return {};
  const details = error.response?.data?.details;
  if (!Array.isArray(details)) return {};
  const out: Record<string, string> = {};
  for (const d of details) {
    if (!d?.path) continue;
    out[d.path.replace(/^body\./, '')] = d.message || 'invalid';
  }
  return out;
};
